import { FilterLines } from '@untitledui/icons';
import { useCallback } from 'react';
import { Dialog as AriaDialog } from 'react-aria-components';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import { DialogTrigger } from '@/lib/base/overlays/modal';
import { Popover } from '@/lib/base/overlays/popover';

import {
  ALL_EVENT_TYPES,
  buildClearedTimelineFilters,
  OTHER_EVENT_TYPES,
} from './timelineFilterDefaults';
import type {
  EventTypeName,
  TimelineFilters as TimelineFiltersType,
} from './types';
import { EVENT_TYPE_CATEGORIES } from './types';

// A missing entry here is a COMPILE error — adding a category to
// EVENT_TYPE_CATEGORIES forces a label (issue acceptance criterion).
const TIMELINE_FILTER_CATEGORY_LABELS: Record<
  keyof typeof EVENT_TYPE_CATEGORIES,
  string
> = {
  lifecycle: 'Lifecycle',
  properties: 'Property changes',
  associations: 'Associations',
  emails: 'Emails',
  linkedInMessages: 'LinkedIn messages',
  calls: 'Calls',
  sms: 'SMS',
  website: 'Website activity',
  payments: 'Payments',
  system: 'System events',
};

const OTHER_ACTIVITY_LABEL = 'Other activity';

interface ActivityTypeRow {
  key: string;
  label: string;
  types: readonly EventTypeName[];
}

// Rows are DERIVED from EVENT_TYPE_CATEGORIES (single source of truth), then
// a transitional "Other activity" row for any enum type with no category.
const ACTIVITY_TYPE_ROWS: ActivityTypeRow[] = [
  ...Object.entries(EVENT_TYPE_CATEGORIES).map(([key, types]) => ({
    key,
    label:
      TIMELINE_FILTER_CATEGORY_LABELS[
        key as keyof typeof EVENT_TYPE_CATEGORIES
      ],
    types,
  })),
  ...(OTHER_EVENT_TYPES.length > 0
    ? [{ key: 'other', label: OTHER_ACTIVITY_LABEL, types: OTHER_EVENT_TYPES }]
    : []),
];

interface TimelineFiltersProps {
  filters: TimelineFiltersType;
  onFiltersChange: (filters: TimelineFiltersType) => void;
  defaultIncludeAssociated?: boolean;
}

interface CategoryFilterRowProps {
  label: string;
  types: readonly EventTypeName[];
  isSelected: boolean;
  onToggle: (types: EventTypeName[]) => void;
}

function CategoryFilterRow({
  label,
  types,
  isSelected,
  onToggle,
}: CategoryFilterRowProps) {
  const handleToggle = useCallback(() => {
    onToggle([...types]);
  }, [onToggle, types]);

  return (
    <div className='px-1.5 py-px'>
      <div className='rounded-md px-2.5 py-2 transition duration-100 ease-linear hover:bg-primary_hover cursor-pointer'>
        <Checkbox
          isSelected={isSelected}
          onChange={handleToggle}
          label={label}
          size='sm'
        />
      </div>
    </div>
  );
}

export function TimelineFilters({
  filters,
  onFiltersChange,
  defaultIncludeAssociated,
}: TimelineFiltersProps) {
  const isMobile = !useBreakpoint('md');

  // Activity type handlers
  const handleActivityTypeToggle = useCallback(
    (eventTypes: EventTypeName[]) => {
      const currentTypes = filters.eventTypes || [];
      const allSelected = eventTypes.every(type => currentTypes.includes(type));

      const newTypes = allSelected
        ? currentTypes.filter(t => !eventTypes.includes(t))
        : ([...new Set([...currentTypes, ...eventTypes])] as EventTypeName[]);

      onFiltersChange({
        ...filters,
        eventTypes: newTypes,
      });
    },
    [filters, onFiltersChange]
  );

  // Associated events toggle handler
  const handleAssociatedToggle = useCallback(() => {
    onFiltersChange({
      ...filters,
      includeAssociated: !filters.includeAssociated,
    });
  }, [filters, onFiltersChange]);

  const handleSelectAll = useCallback(
    () =>
      onFiltersChange(buildClearedTimelineFilters(defaultIncludeAssociated)),
    [onFiltersChange, defaultIncludeAssociated]
  );
  // Empty selection = "no type filter" = show all (the existing contract in
  // fetchViewStatePage + hasActiveTimelineFilters). "None" deselects every box;
  // the timeline is then unfiltered and the badge stays off. Documented,
  // deterministic — never the old accidental calls/SMS-only state.
  const handleSelectNone = useCallback(
    () => onFiltersChange({ ...filters, eventTypes: [] }),
    [filters, onFiltersChange]
  );

  // Count active filters (only count when filters restrict what's shown)
  // Default = all event types selected = no filter applied
  const hasActivityTypeFilters =
    filters.eventTypes &&
    filters.eventTypes.length > 0 &&
    filters.eventTypes.length < ALL_EVENT_TYPES.length;
  const activeFilterCount =
    (hasActivityTypeFilters ? 1 : 0) +
    (filters.startDate ? 1 : 0) +
    (filters.endDate ? 1 : 0) +
    (filters.actorId ? 1 : 0);

  // Check if category groups are selected (derived from filters, not hardcoded)
  const isCategorySelected = useCallback(
    (categoryTypes: readonly EventTypeName[]) =>
      categoryTypes.some(type => filters.eventTypes?.includes(type)),
    [filters.eventTypes]
  );

  return (
    <DialogTrigger>
      {/* Filter Toggle Button — link style to match Add note / Add reminder */}
      <Button
        color='link-color'
        size='xs'
        iconLeading={FilterLines}
        aria-label={isMobile ? 'Filter' : undefined}
      >
        {/* Wrap label + badge in an inline-flex row so the block-level Badge
            stays on the same line instead of wrapping below the label. */}
        <span className='inline-flex items-center gap-1.5'>
          {!isMobile && 'Filter'}
          {activeFilterCount > 0 && (
            <Badge size='sm' color='brand'>
              {activeFilterCount}
            </Badge>
          )}
        </span>
      </Button>

      <Popover placement='bottom end' size='md' className='w-60'>
        {/* The Dialog takes DialogTrigger's aria-controls id and focus, so the
            trigger toggles the popover and Escape closes it (see popover.tsx).
            A plain block: the popover root stays the scroll container, so the
            sticky header still pins to it. */}
        <AriaDialog aria-label='Timeline filters' className='outline-hidden'>
          {/* Sticky header with All / None controls */}
          <div
            data-component='timeline-filter-header'
            className='sticky top-0 z-10 bg-primary flex items-center justify-between px-3 py-2.5 border-b border-secondary_alt'
          >
            <span className='text-sm font-semibold text-secondary'>
              Filters
            </span>
            <div className='flex items-center gap-1'>
              <Button
                color='link-gray'
                size='sm'
                onClick={handleSelectAll}
                className='!text-xs !font-medium'
                data-instance='select-all'
              >
                All
              </Button>
              <span className='text-quaternary text-xs' aria-hidden='true'>
                ·
              </span>
              <Button
                color='link-gray'
                size='sm'
                onClick={handleSelectNone}
                className='!text-xs !font-medium'
                data-instance='select-none'
              >
                None
              </Button>
            </div>
          </div>

          {/* Filter Content */}
          <div className='py-1'>
            {/* Activity Type Filters - Generated from config */}
            <div className='px-1.5'>
              <div className='px-2.5 py-2 mb-1'>
                <span className='text-caption text-quaternary'>
                  Activity type
                </span>
              </div>
              <div>
                {ACTIVITY_TYPE_ROWS.map(({ key, label, types }) => (
                  <CategoryFilterRow
                    key={key}
                    label={label}
                    types={types}
                    isSelected={isCategorySelected(types)}
                    onToggle={handleActivityTypeToggle}
                  />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className='my-1 h-px w-full bg-border-secondary' />

            {/* Related Objects Filter */}
            <div className='px-1.5'>
              <div className='px-2.5 py-2 mb-1'>
                <span className='text-caption text-quaternary'>
                  Related objects
                </span>
              </div>
              <div className='px-1.5 py-px'>
                <div className='rounded-md px-2.5 py-2 transition duration-100 ease-linear hover:bg-primary_hover cursor-pointer'>
                  <Checkbox
                    isSelected={filters.includeAssociated}
                    onChange={handleAssociatedToggle}
                    label='Show related activity'
                    size='sm'
                  />
                </div>
              </div>
            </div>
          </div>
        </AriaDialog>
      </Popover>
    </DialogTrigger>
  );
}
